import { ALIGN_SYNONYM_KEYS, foldHorizontalAlign, foldSynonyms } from './fold-helpers';
import { normalizeWidgetArgs } from './normalize-widget-args';

/**
 * The generic per-widget-type normalization plugin — §4.2 of `ai-mcp-plan.md`.
 *
 * Every widget tool needs the same two things: fold the synonyms a model reaches for onto the
 * schema's real field names, then strip those synonyms off the top level so they don't round-trip
 * as unrecognized noise. Written by hand, that is TWO lists per widget type which must agree:
 * the `foldSynonyms` calls, and a separate `*_FLAT_SYNONYM_KEYS` array. Adding a synonym to one and
 * forgetting the other is silent and nasty in both directions — the synonym folds but also survives
 * as junk, or it is stripped before the fold ever sees it.
 *
 * Here both come from ONE declaration: `flatKeys` is derived from the rules, so they cannot drift.
 * With ~20 widget types still to add (§3.3), that is the difference between a ~40-line file per type
 * and a six-line declaration.
 *
 * What deliberately stays imperative is the bespoke folds (`before`/`after`): image's `image` object
 * unwrap and social's single-entry wrap aren't key renames, they restructure the payload. Forcing
 * them into a declaration would make the declaration a programming language.
 */

/**
 * A fold step. May mutate either object in place, OR return a replacement `data` to carry forward —
 * button's update fold does the latter (`data = { ...firstButton, ...data }` when unwrapping a
 * copied `buttons` array), and a contract that only allowed mutation would drop that silently.
 * Returning nothing means "I mutated in place".
 */
export type WidgetFold = (
    args: Record<string, unknown>,
    data: Record<string, unknown>
) => Record<string, unknown> | void;

/**
 * One "models call it X, the schema calls it Y" rule. `accepts` guards the VALUE, so a wrong-typed
 * synonym can't shadow the real field — a string `"60"` never becomes a numeric height by accident.
 */
export type SynonymRule = {
    target: string;
    synonyms?: readonly string[];
    accepts: (value: unknown) => boolean;
};

export type WidgetNormalizerSpec = {
    rules?: readonly SynonymRule[];
    /**
     * Fold `horizontalAlign`, including the everyday-word values (`left` → `L`). Separate from
     * `rules` because it normalizes the VALUE as well as the key.
     */
    horizontalAlign?: boolean;
    /** Bespoke steps run before the rules — for a fold whose output the rules then read. */
    before?: readonly WidgetFold[];
    /** Bespoke steps run after the rules — for a fold that reads what the rules produced. */
    after?: readonly WidgetFold[];
    /**
     * Top-level keys the bespoke steps consume. Rule targets and their synonyms are added
     * automatically; this is only for keys no rule declares.
     */
    extraFlatKeys?: readonly string[];
    /**
     * `data` carries meaning by its ABSENCE for this widget (a bare indent is a valid 40px spacer),
     * so don't hand the schema an invented `{}` when the caller sent nothing to fold. An explicitly
     * sent empty `data: {}` is left alone — absent and empty stay distinguishable.
     */
    optionalData?: boolean;
};

export type WidgetNormalizer = {
    /** The fold itself, shared by a type's create and update tools. */
    foldData: (args: Record<string, unknown>, data: Record<string, unknown>) => Record<string, unknown>;
    /**
     * `z.preprocess`-ready. `extraFold` runs last and exists for the one shape a declaration can't
     * capture: button, whose create and update disagree about the `buttons` wrapper and so supply
     * their own fold per tool. Like any fold it may return a replacement `data`.
     */
    normalize: (raw: unknown, extraFold?: WidgetFold) => unknown;
    /** Derived, not declared — exported for tests that pin the strip list. */
    flatKeys: readonly string[];
};

function deriveFlatKeys(spec: WidgetNormalizerSpec): readonly string[] {
    const keys = new Set<string>();

    for (const rule of spec.rules ?? []) {
        keys.add(rule.target);
        for (const synonym of rule.synonyms ?? []) {
            keys.add(synonym);
        }
    }

    if (spec.horizontalAlign) {
        keys.add('horizontalAlign');
        for (const key of ALIGN_SYNONYM_KEYS) {
            keys.add(key);
        }
    }

    for (const key of spec.extraFlatKeys ?? []) {
        keys.add(key);
    }

    return [...keys];
}

/**
 * Whether the caller sent a `data` key at all — read off the RAW input, before the scaffolding
 * creates one. Only `optionalData` types care.
 */
function sentData(raw: unknown): boolean {
    return raw != null && typeof raw === 'object' && !Array.isArray(raw) && 'data' in raw;
}

function dropInventedData(normalized: unknown): unknown {
    if (normalized == null || typeof normalized !== 'object' || Array.isArray(normalized)) {
        return normalized;
    }

    const args = normalized as Record<string, unknown>;
    const data = args['data'];

    if (data != null && typeof data === 'object' && !Array.isArray(data) && !Object.keys(data).length) {
        delete args['data'];
    }

    return args;
}

export function createWidgetNormalizer(spec: WidgetNormalizerSpec): WidgetNormalizer {
    const flatKeys = deriveFlatKeys(spec);

    const foldData = (
        args: Record<string, unknown>,
        data: Record<string, unknown>,
        extraFold?: WidgetFold
    ): Record<string, unknown> => {
        // Threaded rather than mutated throughout: a fold is allowed to hand back a DIFFERENT
        // object (see `WidgetFold`), and every later step has to see that one.
        //
        // `replacement()` exists because `WidgetFold` returns `Record | void`, and TypeScript will
        // not narrow `void` away with `??` — a fold that simply mutates in place has no return
        // statement, so `void` has to stay in the signature. The cast is confined to this one line
        // instead of being repeated at each of the three call sites.
        const replacement = (fold: WidgetFold, target: Record<string, unknown>): Record<string, unknown> =>
            (fold(args, target) as Record<string, unknown> | undefined) ?? target;

        let current = data;

        for (const fold of spec.before ?? []) {
            current = replacement(fold, current);
        }

        for (const rule of spec.rules ?? []) {
            foldSynonyms(args, current, rule.target, rule.synonyms ?? [], rule.accepts);
        }

        for (const fold of spec.after ?? []) {
            current = replacement(fold, current);
        }

        if (spec.horizontalAlign) {
            foldHorizontalAlign(args, current);
        }

        if (extraFold) {
            current = replacement(extraFold, current);
        }

        return current;
    };

    return {
        foldData: (args, data) => foldData(args, data),
        flatKeys,
        normalize: (raw, extraFold) => {
            const normalized = normalizeWidgetArgs(raw, (args, data) => foldData(args, data, extraFold), flatKeys);

            return spec.optionalData && !sentData(raw) ? dropInventedData(normalized) : normalized;
        },
    };
}
